import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { ArrowUpRight, Mail, Phone, MapPin, Check, Loader2, MessageSquare, Calendar } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal, MaskedLine } from "@/components/Reveal";
import { CONTACT } from "@/data/site";

const inputCls =
  "w-full rounded-xl border border-border bg-card px-5 py-3.5 text-sm outline-none transition-colors duration-300 placeholder:text-muted-foreground/60 focus:border-coral aria-invalid:border-destructive";

const SERVICES = [
  { value: 'ai', label: 'AI Development' },
  { value: 'n8n', label: 'n8n Automation' },
  { value: 'react', label: 'React Full-Stack' },
  { value: 'not-sure', label: 'Not sure' }
];

const BUDGET_OPTIONS = [
  '< $5k',
  '$5–15k',
  '$15–40k',
  '$40k+',
  'Prefer to discuss'
];

const TIMELINE_OPTIONS = [
  'ASAP',
  '2–4 weeks', 
  '1–3 months',
  'Exploring'
];

export default function Contact() {
  const [searchParams] = useSearchParams();
  
  const [form, setForm] = useState({
    name: "",
    email: "", 
    company: "",
    role: "",
    need: [],
    message: "",
    budget: "",
    timeline: "",
    replyVia: "email",
    whatsapp: "",
    referral: "",
    website: "" // honeypot field
  });

  // Handle query parameters for prefilling form
  useEffect(() => {
    const needParam = searchParams.get('need');
    if (needParam && SERVICES.find(s => s.value === needParam)) {
      setForm(prev => ({
        ...prev,
        need: [needParam]
      }));
    }
  }, [searchParams]);
  
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const updateForm = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
    // Clear error when user starts typing
    if (errors[key]) {
      setErrors(prev => ({ ...prev, [key]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!form.name.trim()) {
      newErrors.name = "Name is required";
    }
    
    if (!form.email.trim()) {
      newErrors.email = "Work email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      newErrors.email = "Use a work email we can reply to";
    }
    
    if (!form.company.trim()) {
      newErrors.company = "Company is required";
    }
    
    if (form.need.length === 0) {
      newErrors.need = "Please select at least one service";
    }
    
    if (!form.message.trim()) {
      newErrors.message = "Project summary is required";
    } else if (form.message.trim().length < 20) {
      newErrors.message = "Add a bit more so we can prepare (≈2 sentences)";
    }
    
    if (form.replyVia === 'whatsapp' && !form.whatsapp.trim()) {
      newErrors.whatsapp = "WhatsApp number is required when selected";
    }
    
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const formErrors = validateForm();
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      // Focus first error field
      const firstErrorField = Object.keys(formErrors)[0];
      document.getElementById(`contact-${firstErrorField}`)?.focus();
      return;
    }
    
    setIsSubmitting(true);
    setSubmitError("");
    
    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(form)
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        if (data.details) {
          setErrors(data.details);
        } else {
          setSubmitError(data.error || 'Something went wrong. Please try again.');
        }
        return;
      }
      
      setIsSubmitted(true);
      
    } catch (error) {
      setSubmitError('Network error. Please try again or email us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <>
        <Seo
          title="Thank You — codeRealm"
          siteName="codeRealm"
          description="Thanks for your message! We'll reply within 1 business day."
        />
        <section className="mx-auto max-w-7xl px-5 pb-24 pt-20 sm:px-8 sm:pt-28">
          <div className="mx-auto max-w-2xl text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-coral/10 text-coral mx-auto mb-8">
              <Check className="h-8 w-8" />
            </div>
            
            <h1 className="font-display text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-5xl">
              Message received.
            </h1>
            
            <p className="mt-6 text-lg leading-relaxed text-muted-foreground">
              Thanks — a human at CodeRealm will reply within <strong>1 business day</strong> (Asia/Kolkata business hours). 
              If it's urgent, use WhatsApp below.
            </p>
            
            <div className="mt-10 flex flex-wrap justify-center gap-4">
              <a
                href="/case-studies"
                className="flex items-center gap-2 rounded-full border border-foreground/20 px-6 py-3 font-mono text-xs uppercase tracking-[0.15em] transition-colors duration-300 hover:border-coral hover:text-coral"
              >
                See case studies
              </a>
              <a
                href="/how-we-work"
                className="flex items-center gap-2 rounded-full border border-foreground/20 px-6 py-3 font-mono text-xs uppercase tracking-[0.15em] transition-colors duration-300 hover:border-coral hover:text-coral"
              >
                How we work
              </a>
              {/* Optional: Book a 20-min intro - add when Calendly URL is available */}
            </div>

            {/* Secondary channels */}
            <div className="mt-16 grid gap-4 sm:grid-cols-2 max-w-lg mx-auto">
              <a
                href={`https://wa.me/${CONTACT.phoneHref.replace('+', '')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center gap-3 rounded-2xl border border-border bg-card p-4 transition-all duration-300 hover:-translate-y-1 hover:border-coral/60"
              >
                <MessageSquare className="h-5 w-5 text-coral" />
                <span className="text-sm font-medium">WhatsApp</span>
              </a>
              <a
                href={`mailto:${CONTACT.email}`}
                className="group flex items-center gap-3 rounded-2xl border border-border bg-card p-4 transition-all duration-300 hover:-translate-y-1 hover:border-coral/60"
              >
                <Mail className="h-5 w-5 text-coral" />
                <span className="text-sm font-medium">Email</span>
              </a>
            </div>
          </div>
        </section>
      </>
    );
  }

  return (
    <>
      <Seo
        title="Contact — codeRealm"
        siteName="codeRealm" 
        description="Tell us what you're building. We reply within 1 business day — AI development, n8n automation and React full-stack engineering."
      />
      <section className="mx-auto max-w-7xl px-5 pb-24 pt-20 sm:px-8 sm:pt-28">
        <MaskedLine>
          <span className="font-mono text-xs uppercase tracking-[0.25em] text-coral">Contact</span>
        </MaskedLine>
        <h1 className="mt-6 font-display text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-6xl lg:text-7xl">
          <MaskedLine delay={0.12}>Tell us what</MaskedLine>
          <MaskedLine delay={0.24}>
            <span className="text-outline">you're building.</span>
          </MaskedLine>
        </h1>
        <Reveal delay={0.35} className="mt-8 max-w-xl">
          <p className="text-base leading-relaxed text-muted-foreground sm:text-lg">
            We reply within <strong>1 business day</strong> (Asia/Kolkata). No mailTo hop — 
            submit here and a human gets back to you.
          </p>
        </Reveal>

        <div className="mt-16 grid gap-14 lg:grid-cols-[1.4fr_1fr]">
          <Reveal>
            <div className="mb-6 text-center">
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground">
                We reply within 1 business day
              </p>
            </div>
            
            <form onSubmit={handleSubmit} data-testid="contact-form" className="grid gap-5">
              {/* Honeypot field - hidden from users */}
              <input
                type="text"
                name="website"
                value={form.website}
                onChange={(e) => updateForm('website', e.target.value)}
                style={{ position: 'absolute', left: '-9999px' }}
                tabIndex="-1"
                autoComplete="off"
              />

              {submitError && (
                <div className="rounded-lg border border-destructive/50 bg-destructive/5 p-4 text-sm text-destructive">
                  {submitError}
                  {submitError.includes('email us directly') && (
                    <span> ({CONTACT.email})</span>
                  )}
                </div>
              )}

              {/* Name and Email */}
              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="contact-name" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                    Name *
                  </label>
                  <input
                    id="contact-name"
                    type="text"
                    value={form.name}
                    onChange={(e) => updateForm('name', e.target.value)}
                    placeholder="Alex Rivera"
                    className={inputCls}
                    aria-invalid={errors.name ? 'true' : 'false'}
                    aria-describedby={errors.name ? 'contact-name-error' : undefined}
                  />
                  {errors.name && (
                    <p id="contact-name-error" className="mt-1 text-sm text-destructive">
                      {errors.name}
                    </p>
                  )}
                </div>
                <div>
                  <label htmlFor="contact-email" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                    Work email *
                  </label>
                  <input
                    id="contact-email"
                    type="email"
                    value={form.email}
                    onChange={(e) => updateForm('email', e.target.value)}
                    placeholder="alex@acme.io"
                    className={inputCls}
                    aria-invalid={errors.email ? 'true' : 'false'}
                    aria-describedby={errors.email ? 'contact-email-error' : undefined}
                  />
                  {errors.email && (
                    <p id="contact-email-error" className="mt-1 text-sm text-destructive">
                      {errors.email}
                    </p>
                  )}
                </div>
              </div>

              {/* Company and Role */}
              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="contact-company" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                    Company *
                  </label>
                  <input
                    id="contact-company"
                    type="text"
                    value={form.company}
                    onChange={(e) => updateForm('company', e.target.value)}
                    placeholder="Acme Ops"
                    className={inputCls}
                    aria-invalid={errors.company ? 'true' : 'false'}
                    aria-describedby={errors.company ? 'contact-company-error' : undefined}
                  />
                  {errors.company && (
                    <p id="contact-company-error" className="mt-1 text-sm text-destructive">
                      {errors.company}
                    </p>
                  )}
                </div>
                <div>
                  <label htmlFor="contact-role" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                    Role
                  </label>
                  <select
                    id="contact-role"
                    value={form.role}
                    onChange={(e) => updateForm('role', e.target.value)}
                    className={inputCls}
                  >
                    <option value="">Select role</option>
                    <option value="founder">Founder</option>
                    <option value="cto">CTO</option>
                    <option value="product">Product</option>
                    <option value="ops">Ops</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>

              {/* What do you need */}
              <div>
                <label className="mb-2 block font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                  What do you need? *
                </label>
                <div className="grid gap-3 sm:grid-cols-2">
                  {SERVICES.map((service) => (
                    <label key={service.value} className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={form.need.includes(service.value)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            updateForm('need', [...form.need, service.value]);
                          } else {
                            updateForm('need', form.need.filter(n => n !== service.value));
                          }
                        }}
                        className="h-4 w-4 rounded border-border bg-card accent-coral"
                      />
                      <span className="text-sm">{service.label}</span>
                    </label>
                  ))}
                </div>
                {errors.need && (
                  <p className="mt-1 text-sm text-destructive">
                    {errors.need}
                  </p>
                )}
              </div>

              {/* Project summary */}
              <div>
                <label htmlFor="contact-message" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                  Project summary *
                </label>
                <textarea
                  id="contact-message"
                  rows={6}
                  value={form.message}
                  onChange={(e) => updateForm('message', e.target.value)}
                  placeholder="We need to replace spreadsheet handoffs between support and billing with monitored n8n workflows — aiming for fewer manual steps in 6-8 weeks."
                  className={`${inputCls} resize-none`}
                  aria-invalid={errors.message ? 'true' : 'false'}
                  aria-describedby={errors.message ? 'contact-message-error' : undefined}
                />
                {errors.message && (
                  <p id="contact-message-error" className="mt-1 text-sm text-destructive">
                    {errors.message}
                  </p>
                )}
              </div>

              {/* Budget and Timeline */}
              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="contact-budget" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                    Budget range <span className="text-muted-foreground/60">(rough range — helps us prepare)</span>
                  </label>
                  <select
                    id="contact-budget"
                    value={form.budget}
                    onChange={(e) => updateForm('budget', e.target.value)}
                    className={inputCls}
                  >
                    <option value="">Select range</option>
                    {BUDGET_OPTIONS.map((option) => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="contact-timeline" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                    Timeline
                  </label>
                  <select
                    id="contact-timeline"
                    value={form.timeline}
                    onChange={(e) => updateForm('timeline', e.target.value)}
                    className={inputCls}
                  >
                    <option value="">Select timeline</option>
                    {TIMELINE_OPTIONS.map((option) => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* How should we reply */}
              <div>
                <label className="mb-2 block font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                  How should we reply?
                </label>
                <div className="flex gap-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="replyVia"
                      value="email"
                      checked={form.replyVia === 'email'}
                      onChange={(e) => updateForm('replyVia', e.target.value)}
                      className="accent-coral"
                    />
                    <span className="text-sm">Email</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="replyVia"
                      value="whatsapp"
                      checked={form.replyVia === 'whatsapp'}
                      onChange={(e) => updateForm('replyVia', e.target.value)}
                      className="accent-coral"
                    />
                    <span className="text-sm">WhatsApp</span>
                  </label>
                </div>
              </div>

              {/* WhatsApp number (conditional) */}
              {form.replyVia === 'whatsapp' && (
                <div>
                  <label htmlFor="contact-whatsapp" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                    WhatsApp number *
                  </label>
                  <input
                    id="contact-whatsapp"
                    type="tel"
                    value={form.whatsapp}
                    onChange={(e) => updateForm('whatsapp', e.target.value)}
                    placeholder="+1234567890"
                    className={inputCls}
                    aria-invalid={errors.whatsapp ? 'true' : 'false'}
                    aria-describedby={errors.whatsapp ? 'contact-whatsapp-error' : undefined}
                  />
                  {errors.whatsapp && (
                    <p id="contact-whatsapp-error" className="mt-1 text-sm text-destructive">
                      {errors.whatsapp}
                    </p>
                  )}
                </div>
              )}

              {/* How you found us */}
              <div>
                <label htmlFor="contact-referral" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                  How you found us
                </label>
                <input
                  id="contact-referral"
                  type="text"
                  value={form.referral}
                  onChange={(e) => updateForm('referral', e.target.value)}
                  placeholder="Google search, referral, etc."
                  className={inputCls}
                />
              </div>

              {/* Submit button */}
              <div className="flex flex-col gap-4">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="group flex items-center justify-center gap-2 rounded-full bg-coral px-8 py-4 font-mono text-xs uppercase tracking-[0.15em] text-white transition-all duration-300 hover:bg-navy disabled:opacity-50 disabled:cursor-not-allowed dark:hover:bg-coral-bright"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      Send message
                      <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </>
                  )}
                </button>
                
                <p className="text-center font-mono text-xs text-muted-foreground">
                  We reply within 1 business day.
                </p>
                
                <p className="text-xs text-muted-foreground">
                  We'll use this to reply about your project. No newsletters unless you ask.
                </p>
              </div>
            </form>
          </Reveal>

          {/* Secondary channels */}
          <div className="flex flex-col gap-5">
            <Reveal delay={0.1}>
              <span className="font-mono text-xs uppercase tracking-[0.25em] text-coral">
                Prefer another channel?
              </span>
            </Reveal>
            
            <Reveal delay={0.15}>
              <a
                href={`https://wa.me/${CONTACT.phoneHref.replace('+', '')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center gap-4 rounded-2xl border border-border bg-card p-6 transition-all duration-300 hover:-translate-y-1 hover:border-coral/60"
              >
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-coral/10 text-coral transition-colors duration-300 group-hover:bg-coral group-hover:text-white">
                  <MessageSquare className="h-5 w-5" />
                </span>
                <span>
                  <span className="block font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">WhatsApp</span>
                  <span className="mt-1 block font-medium">Prefer chat? Message us</span>
                </span>
              </a>
            </Reveal>

            <Reveal delay={0.2}>
              <a
                href={`mailto:${CONTACT.email}`}
                className="group flex items-center gap-4 rounded-2xl border border-border bg-card p-6 transition-all duration-300 hover:-translate-y-1 hover:border-coral/60"
              >
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-coral/10 text-coral transition-colors duration-300 group-hover:bg-coral group-hover:text-white">
                  <Mail className="h-5 w-5" />
                </span>
                <span className="min-w-0">
                  <span className="block font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">Email fallback</span>
                  <span className="mt-1 block truncate text-sm font-medium">{CONTACT.email}</span>
                </span>
              </a>
            </Reveal>

            <Reveal delay={0.25}>
              <div className="rounded-2xl bg-navy p-6 text-white dark:bg-card">
                <p className="font-display text-lg font-bold leading-snug tracking-tight">
                  "Fill this in — we'll reply with a concrete plan, not a sales deck."
                </p>
                <p className="mt-3 font-mono text-[11px] uppercase tracking-[0.2em] text-white/60">
                  Soumitra Mukherjee — CEO
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
